import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Grid,
  InputAdornment,
  Backdrop,
  useTheme,
  MenuItem,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import useMediaQuery from "@mui/material/useMediaQuery";
import Title from "../../Components/Title";
import ShortcutButton from "../../Components/ShortcutButton";
import { PreferencesContext } from "../../PreferencesContext";
import MissingConsumer from "../../Components/MissingConsumer";
import { ExportFilesFunction } from "../../shared/CommonFuctions";
import { KeyboardArrowDownSharp } from "@mui/icons-material";
import SplashComponent from "../../Navigation/splash";
import { StyledMenu } from "../../Components/StyleMenus";
import { GetActiveBuyerByInvoiceDuration } from "../../Services/BuyerService";
import { EncryptDecryptService } from "../../Services/common/EncryptionService";
import { GetActiveBuyerByInvoiceDurationReport } from "../../Services/ReportService";
import { AlertType } from "../../shared/Modal/AlertType";

interface Data {
  buyerName: string;
  type?: string | null;
  days?: number | null;
  saleId?: number | null;
  saleNumber?: string | null;
  saleDate?: string | null;
}

const PartyCustomerInactivityReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const edService = new EncryptDecryptService();
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  const [isLoading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearchValue, setAppliedSearchValue] = useState("");
  const [selectedDuration, setSelectedDuration] = useState("15");
  const [resultData, setResultData] = useState<Data[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const mobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const openExportType = Boolean(anchorEl);

  const handleSearch = useCallback((value: string) => {
    setAppliedSearchValue(value);
  }, []);

  const handleDurationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedDuration(event.target.value);
  };

  const handleInvoiceClick = (saleId?: number | null) => {
    try {
      console.log("Clicked Invoice Sale ID:", saleId);

      if (!saleId) {
        console.log("Sale ID is missing");
        return;
      }

      const encSaleId = edService.encrypt(saleId);

      navigate(
        {
          pathname: "/invoice-save",
          search: `?q=${encodeURIComponent(encSaleId)}&t=tax-invoice`,
        },
        {
          state: {
            title: " Edit Invoice",
            button: "Update",
            saleId: saleId,
          },
        },
      );
    } catch (e: any) {
      console.error("Invoice navigation error:", e);
    }
  };

  const handleClickExportType = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseExportType = () => {
    setAnchorEl(null);
  };

  const TableHeader = React.memo(() => (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{ fontWeight: "bold", minWidth: "180px" }}
          align="left"
        >
          Customer/Party Name
        </TableCell>

        <TableCell
          sx={{ fontWeight: "bold", minWidth: "140px" }}
          align="left"
        >
          Type
        </TableCell>

        <TableCell
          sx={{ fontWeight: "bold", minWidth: "100px" }}
          align="center"
        >
          Days
        </TableCell>

        <TableCell
          sx={{ fontWeight: "bold", minWidth: "150px" }}
          align="left"
        >
          Last Invoice No.
        </TableCell>

        <TableCell
          sx={{ fontWeight: "bold", minWidth: "150px" }}
          align="center"
        >
          Last Invoice Date
        </TableCell>
      </TableRow>
    </TableHead>
  ));

  const TableRowComponent = React.memo(
    ({
      item,
      onInvoiceClick,
    }: {
      item: Data;
      onInvoiceClick: (saleId?: number | null) => void;
    }) => (
      <TableRow hover role="checkbox" tabIndex={-1}>
        <TableCell
          align="left"
          sx={{ fontWeight: "bold" }}
        >
          {item?.buyerName?.toUpperCase()}
        </TableCell>

        <TableCell align="left">
          {item?.type || "-"}
        </TableCell>

        <TableCell align="center"  sx={{ fontWeight: "bold" }}>
          {item?.days ?? "-"}
        </TableCell>

        <TableCell align="left">
          {item?.saleNumber ? (
            <Button
              disableRipple
              sx={{ justifyContent: "left" }}
              onClick={() => onInvoiceClick(item.saleId)}
              className="manuButton-link"
              color="primary"
            >
              <span className="text-link">
                {item.saleNumber}
              </span>
            </Button>
          ) : (
            "-"
          )}
        </TableCell>

        <TableCell align="center">
          {item?.saleDate
            ? new Date(item?.saleDate)
              .toLocaleDateString("en-GB")
              .replace(/\//g, "-")
            : "-"}
        </TableCell>
      </TableRow>
    )
  );

  const fetchPartyCustomerInactivityReport = useCallback(
    async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams();

        params.append(
          "consumerId",
          userContext.consumerId.toString()
        );

        params.append(
          "firmId",
          userContext.firmId.toString()
        );

        params.append(
          "branchId",
          userContext.branchId.toString()
        );

        params.append(
          "financialYear",
          userContext.financialYear
        );

        params.append(
          "timeDuration",
          selectedDuration
        );

        params.append(
          "searchText",
          appliedSearchValue
        );

        const result: any =
          await GetActiveBuyerByInvoiceDuration(params);

        if (
          result &&
          result.data &&
          result.data.data
        ) {
          setResultData(result.data.data);
        } else {
          setResultData([]);
        }

        setLoading(false);
      } catch (e: any) {
        setLoading(false);
        console.log(e);
      }
    },
    [
      userContext.consumerId,
      userContext.firmId,
      userContext.branchId,
      userContext.financialYear,
      selectedDuration,
      appliedSearchValue,
    ]
  );

  async function handelReportPopup(type: any) {
    setAnchorEl2(null);
    setLoading(true);
    let params = new URLSearchParams();
    var attachmentName = "";
    if (type === "pdf") {
      attachmentName = "CustomerPartyInactivityReport.pdf";
    } else {
      attachmentName = "CustomerPartyInactivityReport.xls";
    }
    params.append("reportName", "CustomerPartyInactivityReport");
    params.append("reportType", type);
    params.append("consumerId", userContext.consumerId.toString());
    params.append("firmId", userContext.firmId.toString());
    params.append("branchId", userContext.branchId.toString());
    params.append("searchText", appliedSearchValue);
     params.append("timeDuration", selectedDuration);
    params.append("financialYear", userContext.financialYear.toString());
    params.append("mode", "EXPORT");
    try {
      const result: any = await GetActiveBuyerByInvoiceDurationReport(params);

      if (result && result.data && result.data.data) {
        var fileContent = result?.data?.data?.fileData;
        var attachmentData = fileContent;
        ExportFilesFunction(attachmentName, type, attachmentData);
        setLoading(false);
      }
      setLoading(false);
    } catch (e: any) {
      // setLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    if (userContext.consumerId > 0) {
      fetchPartyCustomerInactivityReport();
    }
  }, [
    userContext.consumerId,
    userContext.firmId,
    userContext.branchId,
    userContext.financialYear,
    selectedDuration,
    appliedSearchValue,
    fetchPartyCustomerInactivityReport,
  ]);

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          backgroundColor: "#fff",
          zIndex: (theme) =>
            theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <SplashComponent />
      </Backdrop>

      <Title pageTitle="Customer/Party Inactivity Report" />

      {userContext.consumerId > 0 ? (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}
        >
          <Grid
            className="title-button-responsive"
            item
            xs={6}
            md={3}
            sx={{
              display: "flex !important",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Grid item xs={6} md={6}>
              <h4 className="page-title">
                Customer/Party Inactivity Reports
              </h4>
            </Grid>

            <Grid
              item
              xs={6}
              md={3}
              marginRight={0}
              textAlign="end"
              paddingTop="0px"
            >
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="medium"
              >
                <ShortcutButton label="ESC" />
                Back
              </Button>
            </Grid>
          </Grid>

          <Grid className="paper top-space">
            <Paper
              elevation={4}
              className="paper-container"
            >
              <Grid
                container
                spacing={1}
                sx={{
                  marginTop: "10px",
                }}
                className="search-space"
              >
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={2}
                  xl={2}
                >
                  <TextField
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch(searchValue);
                      }
                    }}
                    sx={{
                      paddingLeft: "0px !important",
                      width: "100%",
                    }}
                    className="button-responsive-stock"
                    placeholder="Search..."
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(e) =>
                      setSearchValue(e.target.value)
                    }
                    value={searchValue}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleSearch(searchValue)
                            }
                          >
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: searchValue && (
                        <IconButton
                          onClick={() => {
                            setSearchValue("");
                            handleSearch("");
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={9}
                  lg={10}
                  xl={10}
                >
                  <Grid
                    sx={{
                      height: "100%",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "center",
                      gap: "20px",
                      flexDirection: mobileScreen
                        ? "column"
                        : "row",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: mobileScreen
                          ? "100%"
                          : "auto",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Last Order In
                      </Box>

                      <TextField
                        select
                        size="small"
                        label=""
                        value={selectedDuration}
                        onChange={handleDurationChange}
                        sx={{
                          width: mobileScreen
                            ? "100%"
                            : "215px",
                        }}
                      >
                        <MenuItem value="15">
                          15 Days
                        </MenuItem>

                        <MenuItem value="30">
                          30 Days
                        </MenuItem>

                        <MenuItem value="90">
                          90 Days
                        </MenuItem>

                        <MenuItem value="91">
                          90+ Days
                        </MenuItem>

                        <MenuItem value="0">
                          Never Ordered
                        </MenuItem>
                      </TextField>
                    </Box>

                    <Button
                      aria-controls={
                        openExportType
                          ? "customer-party-export-menu"
                          : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={
                        openExportType
                          ? "true"
                          : undefined
                      }
                      disabled={
                        resultData.length <= 0
                      }
                      variant="outlined"
                      disableElevation
                      onClick={
                        handleClickExportType
                      }
                      startIcon={
                        <DownloadIcon />
                      }
                      endIcon={
                        <KeyboardArrowDownSharp />
                      }
                    >
                      Export
                    </Button>

                    <StyledMenu
                      id="customer-party-export-menu"
                      anchorEl={anchorEl}
                      open={openExportType}
                      onClose={
                        handleCloseExportType
                      }
                    >
                      <MenuItem
                        onClick={() =>
                          handelReportPopup("pdf")
                        }
                      >
                        PDF
                      </MenuItem>

                      <MenuItem
                        onClick={() =>
                          handelReportPopup("Excel")
                        }
                      >
                        EXCEL
                      </MenuItem>
                    </StyledMenu>
                  </Grid>
                </Grid>
              </Grid>

              <TableContainer>
                <Table
                  aria-labelledby="tableTitle"
                  size="medium"
                >
                  <TableHeader />

                  {resultData.length > 0 && (
                    <TableBody>
                      {resultData.map(
                        (
                          item: Data,
                          index: number
                        ) => (
                          <TableRowComponent
                            key={`${item.buyerName}-${index}`}
                            item={item}
                            onInvoiceClick={
                              handleInvoiceClick
                            }
                          />
                        )
                      )}
                    </TableBody>
                  )}

                  {resultData.length <= 0 && (
                    <TableBody>
                      <TableRow
                        hover
                        tabIndex={-1}
                      >
                        <TableCell
                          colSpan={5}
                          align="center"
                        >
                          No record(s) found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Box>
      ) : (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}
        >
          <MissingConsumer
            consumerId={
              userContext.consumerId
            }
          />
        </Box>
      )}
    </>
  );
};

export default PartyCustomerInactivityReport;